import type { LucideIcon } from "lucide-react";
import {
  Building2,
  DoorClosed,
  Monitor,
  Users,
  Presentation,
  Building,
  Store,
} from "lucide-react";
import { officeTypeValueToSlug } from "~/components/blocks/Geosearch/locationGeosearch";

export type SpaceTypeCount = {
  spaceType: string;
  count: number;
};

export type SpaceTypeChipStyle = {
  bgClass: string;
  textClass: string;
  Icon: LucideIcon;
  label: string;
};

/** Fallback max chips before "+N more" when width cannot be measured. */
export const DEFAULT_VISIBLE_SPACE_TYPE_CHIPS = 2;

export const SPACE_TYPE_CHIP_GAP_PX = 6;
export const SPACE_TYPE_CHIP_CLASS =
  "inline-flex items-center gap-1.5 rounded-2xl px-3 py-1.5 text-xs font-medium whitespace-nowrap flex-shrink-0";
export const SPACE_TYPE_CHIP_BUTTON_CLASS = `${SPACE_TYPE_CHIP_CLASS} relative z-[11] cursor-pointer hover:opacity-90 transition-opacity`;

function normalizeSpaceTypeKey(spaceType: string): string {
  return spaceType.trim().toLowerCase();
}

/** Map API / display space-type labels to internal filter values. */
function spaceTypeToInternalValue(spaceType: string): string {
  const key = normalizeSpaceTypeKey(spaceType);

  if (key.includes("serviced") || key.includes("managed")) return "serviced";
  if (key.includes("coworking")) return "coworking";
  if (key.includes("private")) return "private";
  if (key.includes("shared")) return "shared";
  if (key.includes("enterprise")) return "enterprise";
  if (key.includes("sublet") || key.includes("sublease")) return "sublet";
  if (
    key.includes("traditional") ||
    key.includes("commercial") ||
    key.includes("conventional")
  ) {
    return "conventional";
  }
  if (key.includes("virtual")) return "virtual";

  return key.replace(/\s+/g, "-");
}

/** URL slug for `?workspaceType=` (e.g. Private Office → private-office). */
export function spaceTypeToWorkspaceTypeSlug(spaceType: string): string {
  if (!spaceType?.trim()) return "";
  const internal = spaceTypeToInternalValue(spaceType);
  return officeTypeValueToSlug(internal) ?? internal;
}

/** Append or replace `workspaceType` on a property detail URL. */
export function appendWorkspaceTypeToPropertyLink(
  propertyLink: string,
  spaceType: string,
): string {
  if (!propertyLink) return "";
  const slug = spaceTypeToWorkspaceTypeSlug(spaceType);
  if (!slug) return propertyLink;

  const [path = "", existingQuery = ""] = propertyLink.split("?");
  const params = new URLSearchParams(existingQuery);
  params.set("workspaceType", slug);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Short label for chip display (matches card design). */
export function getSpaceTypeChipLabel(spaceType: string): string {
  const key = normalizeSpaceTypeKey(spaceType);
  const labelMap: Record<string, string> = {
    "serviced office": "Serviced Office",
    "serviced office (classic)": "Serviced Office",
    "serviced office (creative)": "Serviced Office",
    "coworking space": "Coworking",
    "coworking desk": "Coworking",
    coworking: "Coworking",
    "private office": "Private Office",
    private: "Private Office",
    "meeting room": "Meeting Rooms",
    "meeting rooms": "Meeting Rooms",
    "virtual office": "Virtual Office",
    virtual: "Virtual Office",
    "shared office": "Shared Office",
    "shared space": "Shared Office",
    "shared workspace": "Shared Office",
    "enterprise office": "Enterprise",
    "enterprise suite": "Enterprise",
    "managed space": "Managed",
    "managed office space": "Managed",
    "sublet office": "Sublease",
    "sublease office": "Sublease",
    "sublet office space (not shared)": "Sublease",
    "traditional office": "Traditional",
    "commercial workspace": "Traditional",
    "conventional workspace": "Traditional",
  };

  return labelMap[key] ?? spaceType;
}

/**
 * Pastel chip colors/icons keyed by space type
 * (Private Office, Coworking, Serviced, Meeting Rooms, Virtual, etc.).
 */
export function getSpaceTypeChipStyle(spaceType: string): SpaceTypeChipStyle {
  const key = normalizeSpaceTypeKey(spaceType);
  const label = getSpaceTypeChipLabel(spaceType);

  if (
    key.includes("serviced") ||
    key === "managed" ||
    key.includes("managed space") ||
    key.includes("managed office")
  ) {
    return {
      bgClass: "bg-oh-dusty-rose",
      textClass: "text-oh-raspberry-rose",
      Icon: Building2,
      label,
    };
  }

  if (key.includes("coworking")) {
    return {
      bgClass: "bg-oh-warm-amber",
      textClass: "text-oh-honey-amber",
      Icon: Users,
      label,
    };
  }

  if (key.includes("meeting")) {
    return {
      bgClass: "bg-amber-50",
      textClass: "text-amber-700",
      Icon: Presentation,
      label,
    };
  }

  if (key.includes("virtual")) {
    return {
      bgClass: "bg-oh-cloudy-slate",
      textClass: "text-oh-muted-slate",
      Icon: Monitor,
      label,
    };
  }

  if (key.includes("private")) {
    return {
      bgClass: "bg-oh-soft-plum",
      textClass: "text-oh-deep-plum",
      Icon: DoorClosed,
      label,
    };
  }

  if (key.includes("shared")) {
    return {
      bgClass: "bg-oh-dusty-mauve",
      textClass: "text-oh-muted-mauve",
      Icon: Users,
      label,
    };
  }

  if (key.includes("enterprise")) {
    return {
      bgClass: "bg-oh-faded-sky",
      textClass: "text-oh-soft-sky",
      Icon: Building,
      label,
    };
  }

  if (key.includes("sublet") || key.includes("sublease")) {
    return {
      bgClass: "bg-oh-dusty-sage",
      textClass: "text-oh-muted-sage",
      Icon: Building2,
      label,
    };
  }

  if (
    key.includes("traditional") ||
    key.includes("commercial") ||
    key.includes("conventional")
  ) {
    return {
      bgClass: "bg-oh-dusty-taupe",
      textClass: "text-oh-muted-taupe",
      Icon: Building,
      label,
    };
  }

  if (key.includes("retail")) {
    return {
      bgClass: "bg-rose-50",
      textClass: "text-rose-700",
      Icon: Store,
      label,
    };
  }

  return {
    bgClass: "bg-gray-100",
    textClass: "text-gray-700",
    Icon: Building2,
    label,
  };
}

export function getSpaceTypeChipLabelText(item: SpaceTypeCount) {
  const { label } = getSpaceTypeChipStyle(item.spaceType);
  return item.count > 0 ? `${label} (${item.count})` : label;
}

/** Safely read `spaceTypeCounts` from API listing payloads. */
export function getSpaceTypeCountsFromListing(
  listing: unknown,
): SpaceTypeCount[] | undefined {
  if (!listing || typeof listing !== "object") return undefined;
  const raw = (listing as { spaceTypeCounts?: unknown }).spaceTypeCounts;
  if (!Array.isArray(raw)) return undefined;

  const counts = raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const spaceType = (item as { spaceType?: unknown }).spaceType;
      const count = (item as { count?: unknown }).count;
      if (typeof spaceType !== "string" || !spaceType.trim()) return null;
      return {
        spaceType,
        count: typeof count === "number" ? count : Number(count) || 0,
      };
    })
    .filter((item): item is SpaceTypeCount => item != null);

  return counts.length ? counts : undefined;
}
